import { SerializedStyles } from "@emotion/react";
import { Ref, memo, useCallback } from "react";
import { useNoSubmit } from "src/apps/AppFlow/pages/hooks";
import { styles } from "src/apps/AppFlow/pages/styles";
import { FormFlowPage, IllustrativeOptions, TextInput } from "src/components";
import { createStyles } from "src/styles";
import { DebtTypeAndLenderNameValue } from "src/types/Application";

import { enums, formatters } from "@fraction/shared";

const { DEBT_TYPE_MAP } = formatters.enums;

export interface DebtTypeAndLenderNameProps {
  onChange: (value: DebtTypeAndLenderNameValue) => void;
  value?: DebtTypeAndLenderNameValue;
  debtTypes?: enums.DebtType[];

  focusRef?: Ref<HTMLInputElement>;
  onNext: () => void;
  onBack: () => void;
  style?: SerializedStyles;
}

const DEBT_TYPES_DEFAULT: enums.DebtType[] = [
  enums.DebtType.CREDIT_CARD,
  enums.DebtType.PERSONAL_LOAN,
  enums.DebtType.CAR_LOAN,
  enums.DebtType.LINE_OF_CREDIT,
  enums.DebtType.OVERDRAFT,
  enums.DebtType.STUDENT_LOAN,
  enums.DebtType.OTHER,
];

const OPTION_STYLES = createStyles({
  options: {
    marginBottom: "24px",
  },
});

const DebtTypeAndLenderName = ({
  onNext,
  onBack,
  style,
  focusRef,
  value,
  onChange,
  debtTypes = DEBT_TYPES_DEFAULT,
  ...props
}: DebtTypeAndLenderNameProps) => {
  const validate = useCallback(() => value?.lenderName !== undefined && value?.type !== undefined, [value]);
  const { handleNext, nextDisabled } = useNoSubmit({ onNext, validate });

  const handleChangeLenderName = useCallback(
    (lenderName?: string) => {
      onChange({
        ...value,
        lenderName,
      });
    },
    [onChange, value]
  );

  const handleChangeType = useCallback(
    (type?: enums.DebtType) => {
      onChange({
        ...value,
        type,
      });
    },
    [onChange, value]
  );

  return (
    <FormFlowPage
      {...props}
      title="Debt type and lender name"
      description="What sort of debt is it and who do you owe it to?"
      onNext={handleNext}
      onBack={onBack}
      style={style}
      nextDisabled={nextDisabled}
    >
      <IllustrativeOptions
        label="Liability type"
        value={value?.type}
        onChange={handleChangeType}
        options={debtTypes.map((type) => ({ value: type, label: DEBT_TYPE_MAP[type] }))}
        containerStyle={OPTION_STYLES.options}
      />
      <TextInput
        ref={focusRef}
        style={styles.topInput}
        name="lenderName"
        label="Creditor name"
        value={value?.lenderName || ""}
        onChange={handleChangeLenderName}
      />
    </FormFlowPage>
  );
};

export default memo(DebtTypeAndLenderName);
